
import { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Navigation from "@/components/Navigation";
import { Toaster } from "@/components/ui/toaster";
import { trackPageView } from "@/utils/analytics";
import { ChatInterfaceProvider } from "@/contexts/ChatInterfaceContext";
import { capture, identify } from "@/lib/posthog";
import { useAuth } from "@/contexts/AuthContext";
import CookieConsent from "@/components/compliance/CookieConsent";

const Root = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Identify logged-in user for PostHog
  useEffect(() => {
    if (user?.id) {
      identify(user.id, { email: (user as any)?.email });
    }
  }, [user?.id]);

  useEffect(() => {
    // Track page view on route change (internal analytics)
    const pageName = location.pathname === "/" ? "home" : location.pathname.slice(1);
    trackPageView(pageName);
    
    // PostHog pageview with basic UTM capture
    const url = new URL(window.location.href);
    const utm_source = url.searchParams.get('utm_source') || undefined;
    const utm_medium = url.searchParams.get('utm_medium') || undefined;
    const utm_campaign = url.searchParams.get('utm_campaign') || undefined;
    const utm_content = url.searchParams.get('utm_content') || undefined;
    const utm_term = url.searchParams.get('utm_term') || undefined;

    capture('$pageview', {
      path: location.pathname + location.search + location.hash,
      referrer: document.referrer || undefined,
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_term,
    });
    
    // Log for debugging

    // Handle hash scrolling on initial load and route changes
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    } else if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  // Determine if we're on dashboard pages that handle their own spacing
  const isDashboardPage = location.pathname.startsWith('/dashboard') ||
                         location.pathname.startsWith('/admin') ||
                         location.pathname === '/chat-history' ||
                         location.pathname === '/creator-dashboard' ||
                         location.pathname.startsWith('/chat/') ||
                         location.pathname.startsWith('/update-posit/') ||
                         location.pathname.startsWith('/p/') ||
                         location.pathname.startsWith('/public-posits') ||
                         location.pathname.startsWith('/bug-reports') ||
                         location.pathname.startsWith('/prompt-tester') ||
                         location.pathname.startsWith('/ai-testing-dashboard');

  return (
    <ChatInterfaceProvider>
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className={`w-full ${isDashboardPage ? '' : 'pt-16'}`}>
          <CookieConsent />
          <Outlet />
        </main>
        <Toaster />
      </div>
    </ChatInterfaceProvider>
  );
};

export default Root;
